import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { toast } from 'react-toastify';

import type { PmsType, RentManagerConfig } from '../../types';
import type { Unit } from '@/Units/types';
import { updateUnitPmsConfig } from '@/Units/api';
import { naturalCompare } from '@/util';

const useStyles = makeStyles(() => ({
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  fieldValue: {
    color: '#666',
  },
  placeholder: {
    color: '#999',
    fontStyle: 'italic',
  },
}));

interface PmsSettingsProps {
  pmsType: PmsType | null;
  pmsConfig: RentManagerConfig | null;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: RentManagerConfig | null) => Promise<void>;
  onRefresh?: () => void;
}

export const PmsSettings = ({
  pmsType,
  pmsConfig,
  units,
  onUpdate,
  onRefresh,
}: PmsSettingsProps): JSX.Element => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Local form state
  const [localPmsType, setLocalPmsType] = React.useState<PmsType | ''>(pmsType || '');
  const [localPropertyShortName, setLocalPropertyShortName] = React.useState(
    pmsConfig?.property_short_name || '',
  );
  const [localChargeType, setLocalChargeType] = React.useState(
    pmsConfig?.default_charge_type || '',
  );
  const [localUnitMappings, setLocalUnitMappings] = React.useState<Record<string, string>>({});

  // Build unit mappings from units[].pms_config.unit_name
  const buildMappingsFromUnits = React.useCallback(() => {
    const mappings: Record<string, string> = {};
    units.forEach((u) => {
      if (u.pms_config?.unit_name) {
        mappings[u.id] = u.pms_config.unit_name;
      }
    });
    return mappings;
  }, [units]);

  // Reset local state when props change
  React.useEffect(() => {
    setLocalPmsType(pmsType || '');
    setLocalPropertyShortName(pmsConfig?.property_short_name || '');
    setLocalChargeType(pmsConfig?.default_charge_type || '');
    setLocalUnitMappings(buildMappingsFromUnits());
    setIsEditing(false);
  }, [pmsType, pmsConfig, buildMappingsFromUnits]);

  const billableUnits = React.useMemo(
    () =>
      units
        .filter((u) => {
          const flags = (u.unit_flags ?? []) as string[];
          return !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
        })
        .sort((a, b) => naturalCompare(a.name, b.name)),
    [units],
  );

  const mappedCount = React.useMemo(
    () => billableUnits.filter((u) => localUnitMappings[u.id]).length,
    [billableUnits, localUnitMappings],
  );

  const handleSave = async () => {
    if (localPmsType === '') {
      // Clearing PMS config
      setIsSaving(true);
      try {
        await onUpdate(null, null);
        // Clear unit PMS config
        await Promise.all(
          billableUnits
            .filter((u) => u.pms_config)
            .map((u) => updateUnitPmsConfig(u.id, null, null)),
        );
        setIsEditing(false);
        onRefresh?.();
        toast.success('PMS settings cleared', { closeButton: true });
      } catch {
        resetToProps();
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!localPropertyShortName.trim()) {
      toast.error('Property Short Name is required', { closeButton: true });
      return;
    }

    setIsSaving(true);
    try {
      // Save structure-level config
      const config: RentManagerConfig = {
        property_short_name: localPropertyShortName.trim(),
        default_charge_type: localChargeType.trim() || undefined,
      };
      await onUpdate(localPmsType as PmsType, config);

      // Save unit PMS config
      const currentMappings = buildMappingsFromUnits();
      const savePromises: Promise<void>[] = [];
      for (const unit of billableUnits) {
        const newName = localUnitMappings[unit.id] || null;
        const oldName = currentMappings[unit.id] || null;
        if (newName !== oldName) {
          savePromises.push(
            updateUnitPmsConfig(
              unit.id,
              newName ? 'RENT_MANAGER' : null,
              newName ? { unit_name: newName } : null,
            ),
          );
        }
      }
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      setIsEditing(false);
      onRefresh?.();
      toast.success('PMS settings updated successfully', { closeButton: true });
    } catch {
      resetToProps();
    } finally {
      setIsSaving(false);
    }
  };

  const resetToProps = () => {
    setLocalPmsType(pmsType || '');
    setLocalPropertyShortName(pmsConfig?.property_short_name || '');
    setLocalChargeType(pmsConfig?.default_charge_type || '');
    setLocalUnitMappings(buildMappingsFromUnits());
  };

  const handleCancel = () => {
    resetToProps();
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    // Pre-fill unit mappings for unmapped units with their SimpleSub names
    const mappings = { ...localUnitMappings };
    billableUnits.forEach((unit) => {
      if (!mappings[unit.id]) {
        mappings[unit.id] = unit.name;
      }
    });
    setLocalUnitMappings(mappings);
    setIsEditing(true);
  };

  const handleUnitMappingChange = (unitId: string, rmName: string) => {
    setLocalUnitMappings((prev) => ({ ...prev, [unitId]: rmName }));
  };

  return (
    <Card sx={{ mt: 2, mb: 8 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PMS Export Settings
          </Typography>
          {!isEditing && (
            <IconButton onClick={handleStartEdit} className={classes.editIcon} size="small">
              <EditIcon />
            </IconButton>
          )}
          {isEditing && (
            <>
              <IconButton onClick={handleSave} disabled={isSaving} size="small" color="primary">
                {isSaving ? <CircularProgress size={20} /> : <CheckIcon />}
              </IconButton>
              <IconButton onClick={handleCancel} disabled={isSaving} size="small">
                <CloseIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Configure Property Management System integration to export billing data as a CSV that can
          be directly imported into your PMS.
        </Typography>

        {/* PMS Type */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            PMS Type
          </Typography>
          {isEditing ? (
            <TextField
              select
              fullWidth
              size="small"
              value={localPmsType}
              onChange={(e) => setLocalPmsType(e.target.value as PmsType | '')}
              disabled={isSaving}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              <option value="RENT_MANAGER">Rent Manager</option>
            </TextField>
          ) : (
            <Typography className={pmsType ? classes.fieldValue : classes.placeholder}>
              {pmsType === 'RENT_MANAGER' ? 'Rent Manager' : 'Not configured'}
            </Typography>
          )}
        </Box>

        {/* Show config fields only when a PMS type is selected */}
        {(isEditing ? localPmsType !== '' : pmsType !== null) && (
          <>
            {/* Property Short Name */}
            <Box sx={{ mb: 3 }}>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Property Short Name
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={localPropertyShortName}
                  onChange={(e) => setLocalPropertyShortName(e.target.value)}
                  placeholder="e.g., Oak Apartments"
                  helperText="Must match the Property Short Name in Rent Manager"
                  disabled={isSaving}
                />
              ) : (
                <Typography className={classes.fieldValue}>
                  {pmsConfig?.property_short_name || 'Not set'}
                </Typography>
              )}
            </Box>

            {/* Default Charge Type */}
            <Box sx={{ mb: 3 }}>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Default Charge Type
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={localChargeType}
                  onChange={(e) => setLocalChargeType(e.target.value)}
                  placeholder="e.g., Water"
                  helperText="Optional charge type code for Rent Manager (leave blank to set during import)"
                  disabled={isSaving}
                />
              ) : (
                <Typography
                  className={
                    pmsConfig?.default_charge_type ? classes.fieldValue : classes.placeholder
                  }
                >
                  {pmsConfig?.default_charge_type || 'Not set'}
                </Typography>
              )}
            </Box>

            {/* Unit Name Mappings */}
            <Box>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Unit Name Mappings
              </Typography>
              {!isEditing && (
                <Typography className={classes.fieldValue} sx={{ mb: 1 }}>
                  {mappedCount > 0
                    ? `${mappedCount} of ${billableUnits.length} units mapped`
                    : 'No mappings configured'}
                </Typography>
              )}
              {isEditing && (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>SimpleSub Unit Name</TableCell>
                        <TableCell>Rent Manager Unit Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billableUnits.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell>
                            <Typography variant="body2">{unit.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={localUnitMappings[unit.id] || ''}
                              onChange={(e) => handleUnitMappingChange(unit.id, e.target.value)}
                              disabled={isSaving}
                              placeholder={unit.name}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
